// Fill-only theme fan-out: plan which of a client's preferences should
// receive a theme. Pure functions. No React, no network.
//
// Why fan-out exists: a theme is stored per-preference (the "Option B"
// attachment decision), but brand is a client-level idea. A client with
// 27 preference sets would otherwise have to re-theme 27 times to look
// consistent. Fan-out turns that into one gesture.
//
// Why FILL-ONLY (PM, 2026-07-20): fan-out writes the theme ONLY into
// preferences that carry no theme at all. A preference already themed
// is left untouched, so the gesture can never clobber a deliberately
// different look -- for example a simple-mode embed themed on purpose.
//
// The cost of that guarantee: a RE-BRAND does not propagate. Once
// preferences are themed, a second fan-out skips every one of them.
//
// So the planner reports BOTH buckets, and both carry the properties
// that would be written. The fill-only bucket is what the default action
// applies; the skipped bucket is what an explicit opt-in can additionally
// replace. Deciding that here would force the choice before anyone can
// see which looks are at stake -- the planner stays a pure description
// of the options and the UI chooses, at the point where the affected
// preferences are named on screen.

import { normalize } from '@invintusmedia/preference-model-manager';

import { THEME_KEYS, hasTheme } from './themeTokens';

/**
 * Decide what a fan-out would do, without doing any of it.
 *
 * @param {Array<{prefID: any, playerPref: object, default?: boolean}>} rows
 *        the client's preference rows, as returned by the list endpoint
 * @param {Object<string,string>} theme  the 9 theme keys to propagate
 * @param {any} currentPrefID            the preference being edited; excluded
 *                                       from both lists since it is the source
 * @returns {{
 *   targets: Array<{prefID: any, name: string, properties: object}>,
 *   skipped: Array<{prefID: any, name: string, properties: object}>,
 *   unreadable: Array<{prefID: any}>,
 * }}
 *
 * `skipped` entries carry `properties` too -- already themed with the
 * new values -- so a caller offering "replace these as well" can write
 * them without re-planning.
 */
export function planFanOut (rows, theme, currentPrefID) {
  const targets = [];
  const skipped = [];
  const unreadable = [];

  (rows || []).forEach(row => {
    if (!row) return;

    // Compare loosely: prefID arrives as a number from the list
    // endpoint but as a string from the query string.
    /* eslint-disable-next-line eqeqeq */
    if (currentPrefID != null && row.prefID == currentPrefID) return;

    let properties;
    try {
      properties = normalize(row.playerPref);
    } catch (e) {
      // normalize throws TypeError on a non-object row. One malformed
      // preference must not abort the whole fan-out, but it must not
      // be silently counted as a success either.
      unreadable.push({ prefID: row.prefID });
      return;
    }

    const name = (row.playerPref && typeof row.playerPref.name === 'string')
      ? row.playerPref.name
      : '';

    if (hasTheme(properties)) {
      skipped.push({
        prefID: row.prefID,
        name,
        properties: applyTheme(properties, theme),
      });
      return;
    }

    targets.push({
      prefID: row.prefID,
      name,
      properties: applyTheme(properties, theme),
    });
  });

  return { targets, skipped, unreadable };
}

/**
 * Overlay the 9 theme keys onto a normalized bag, leaving every other
 * property untouched. Returns a new object -- fan-out must never mutate
 * a bag the editor is still rendering.
 */
export function applyTheme (properties, theme) {
  const next = { ...properties };
  THEME_KEYS.forEach(key => {
    const value = theme ? theme[key] : '';
    next[key] = typeof value === 'string' ? value : '';
  });
  return next;
}

/**
 * Read the rows out of the list response.
 *
 * The list endpoint returns preferences keyed by ID rather than as an
 * array, which is why the manager page runs them through `_.toArray`.
 * Mirrored here so fan-out does not depend on the manager's store.
 */
export function rowsFromResponse (response) {
  const data = response && response.data;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Object.keys(data).map(k => data[k]).filter(v => v && typeof v === 'object');
}
